/**
 * Wizard helper: builds the final intermediate product orders from the forecasts + master data.
 *
 * Both results mirror the same list 1:1 - only in different formats:
 *  - rows:       display in Step4Review (orderId, productName, baseUnit, lotSize)
 *  - saveOrders: API payload for ProductionScheduleDto.intermediateOrders
 *
 * The splitting logic lives in intermediateOrderSplittingService.splitForecastQuantity (maxLotSize,
 * minLotSize, lotSizeMultiple). Every forecast entry produces 1..n lots, each with its own
 * running sequence per material and its own orderId in the format
 * `{yyyymmdd}_{schedulePrefix}_{slug}_{seq}` - byte for byte identical with
 * IntermediateProductOrderService.buildIntermediateOrderId in the backend.
 */
import { splitForecastQuantity } from '../services/intermediateOrderSplittingService';
import { unitLabel } from './unitLabel';
import type { ManualOrder } from '../api/models/ManualOrder';

export interface IntermediateOrderRow {
  orderId: string;
  productName: string;
  baseUnit: string;
  lotSize: number;
}

interface ForecastInput {
  materialId?: string | null;
  materialName?: string | null;
  demandDate?: string | null;
  quantity?: number | null;
}

interface MaterialInput {
  uuid?: string;
  name?: string;
  baseUnit?: string;
  materialType?: string;
  maxLotSize?: number | null;
  minLotSize?: number | null;
  lotSizeMultiple?: number | null;
}

const SLUG_MAX = 30;

function toSlug(name: string | null | undefined, materialIdFallback: string): string {
  if (!name?.trim()) return materialIdFallback.replace(/-/g, '').substring(0, 8);
  const s = name.trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (s.length === 0) return materialIdFallback.replace(/-/g, '').substring(0, 8);
  return s.length > SLUG_MAX ? s.substring(0, SLUG_MAX) : s;
}

export function buildIntermediateOrders(input: {
  forecasts: ForecastInput[];
  materials: MaterialInput[];
  planStart: string | null | undefined; // YYYY-MM-DD
  planEnd: string | null | undefined;   // YYYY-MM-DD
  schedulePrefix: string;                // 8 hex chars from schedule UUID
}): { rows: IntermediateOrderRow[]; saveOrders: ManualOrder[] } {
  const intermediateMaterials = new Map<string, MaterialInput>();
  input.materials.forEach((m) => {
    if (m?.materialType === 'INTERMEDIATE_PRODUCT' && m.uuid) {
      intermediateMaterials.set(m.uuid, m);
    }
  });
  if (intermediateMaterials.size === 0) {
    return { rows: [], saveOrders: [] };
  }

  const filtered = input.forecasts
    .filter((d) => d.materialId && intermediateMaterials.has(d.materialId))
    .filter((d) => {
      const dStr = d.demandDate?.split('T')[0];
      if (!dStr) return false;
      if (input.planStart && dStr < input.planStart) return false;
      if (input.planEnd && dStr > input.planEnd) return false;
      return true;
    });

  // Count the sequence up per material, sorted by demandDate - the same way as the
  // campaignCounter in batchDeterminationData.
  filtered.sort((a, b) => {
    const aId = a.materialId ?? '';
    const bId = b.materialId ?? '';
    if (aId !== bId) return aId.localeCompare(bId);
    return (a.demandDate ?? '').localeCompare(b.demandDate ?? '');
  });

  const rows: IntermediateOrderRow[] = [];
  const saveOrders: ManualOrder[] = [];
  const seqByMaterial = new Map<string, number>();

  for (const d of filtered) {
    const meta = intermediateMaterials.get(d.materialId!)!;
    const dateStr = d.demandDate?.split('T')[0]?.replace(/-/g, '') ?? '';
    const slug = toSlug(meta.name ?? d.materialName ?? '', d.materialId ?? '');
    const productName = meta.name ?? d.materialName ?? d.materialId ?? '';
    const baseUnit = unitLabel(meta.baseUnit);

    const lots = splitForecastQuantity(Number(d.quantity ?? 0), {
      maxLotSize: meta.maxLotSize,
      minLotSize: meta.minLotSize,
      lotSizeMultiple: meta.lotSizeMultiple,
    });

    for (const lotSize of lots) {
      const seq = (seqByMaterial.get(d.materialId!) ?? 0) + 1;
      seqByMaterial.set(d.materialId!, seq);
      const orderId = `${dateStr}_${input.schedulePrefix}_${slug}_${seq}`;
      rows.push({ orderId, productName, baseUnit, lotSize });
      saveOrders.push({
        intermediateProductUuid: d.materialId!,
        lotSize,
        orderNumber: orderId,
      });
    }
  }

  rows.sort((a, b) => {
    const byName = a.productName.localeCompare(b.productName, undefined, { sensitivity: 'base' });
    if (byName !== 0) return byName;
    return a.orderId.localeCompare(b.orderId);
  });

  return { rows, saveOrders };
}
